import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import {
  filterPortalPromotions,
  mapPortalPromotionsFromApi,
  toPortalPromotionSlug,
} from "@/data/portal_promotion_mappers";
import { fetchPortalPromotionsMock } from "@/data/portal_promotions_mock";
import { V2PromotionService } from "@/services/v2-promotion.service";
import {
  extractV2PromotionItem,
  extractV2PromotionsList,
  resolvePromotionIdFromSlug,
} from "@/utils/v2PromotionApi";

export function usePortalPromotions({ loadOnMount = true } = {}) {
  const store = useStore();
  const { t } = useI18n();

  const promotions = ref([]);
  const activeFilter = ref("all");
  const rawPromotions = ref([]);
  const promotionsUseMock = ref(false);
  const signingInPromotionId = ref(null);
  const promotionsLoading = ref(false);

  const currentLocale = computed(() => store.getters.getCurrentLocale);

  const showPromotionToast = (message, type = "success") => {
    toast(message, {
      type,
      theme: "colored",
      transition: toast.TRANSITIONS.SLIDE,
      position: toast.POSITION.BOTTOM_RIGHT,
    });
  };

  const setPromotionSignedIn = (promotionId, signedIn = true) => {
    rawPromotions.value = rawPromotions.value.map((promotion) =>
      promotion.id === promotionId
        ? { ...promotion, signed_in: signedIn }
        : promotion,
    );
    applyPromotionLocale();
  };

  const applyPromotionLocale = () => {
    promotions.value = mapPortalPromotionsFromApi(
      rawPromotions.value,
      currentLocale.value,
    );
  };

  const loadPromotions = async () => {
    promotionsLoading.value = true;
    let loadedFromApi = false;

    try {
      const response = await V2PromotionService.available();
      const payload = extractV2PromotionsList(response);

      if (payload !== null) {
        promotionsUseMock.value = false;
        rawPromotions.value = payload;
        loadedFromApi = true;
      }
    } catch (error) {
      console.warn("Portal promotions API unavailable, using mock data.", error);
    }

    if (!loadedFromApi) {
      promotionsUseMock.value = true;
      rawPromotions.value = await fetchPortalPromotionsMock();
    }

    applyPromotionLocale();
    promotionsLoading.value = false;
  };

  const handlePromotionSignIn = async (promotionId) => {
    if (signingInPromotionId.value != null) {
      return;
    }

    signingInPromotionId.value = promotionId;

    try {
      if (!promotionsUseMock.value) {
        const response = await V2PromotionService.signIn(promotionId);

        if (response?.status === 201 || response?.status === 200) {
          setPromotionSignedIn(promotionId, true);
          showPromotionToast(t("portalHome.promotions.signInSuccess"));
          return;
        }

        const message =
          response?.data?.message || t("portalHome.promotions.signInError");
        showPromotionToast(message, "error");
        return;
      }

      setPromotionSignedIn(promotionId, true);
      showPromotionToast(t("portalHome.promotions.signInSuccess"));
    } catch (error) {
      console.warn("Promotion sign-in failed.", error);
      showPromotionToast(t("portalHome.promotions.signInError"), "error");
    } finally {
      signingInPromotionId.value = null;
    }
  };

  const findPromotionBySlug = (slug) =>
    promotions.value.find((item) => item.slug === slug) ?? null;

  const upsertRawPromotion = (item) => {
    const index = rawPromotions.value.findIndex(
      (promotion) => promotion.id === item.id,
    );

    if (index === -1) {
      rawPromotions.value = [...rawPromotions.value, item];
    } else {
      rawPromotions.value = rawPromotions.value.map((promotion, itemIndex) =>
        itemIndex === index ? { ...promotion, ...item } : promotion,
      );
    }

    applyPromotionLocale();
  };

  const loadPromotionById = async (promotionId) => {
    if (promotionsUseMock.value) {
      applyPromotionLocale();
      return promotions.value.find((item) => item.id === promotionId) ?? null;
    }

    try {
      const response = await V2PromotionService.show(promotionId);
      const item = extractV2PromotionItem(response);

      if (item) {
        upsertRawPromotion(item);
        return promotions.value.find((mapped) => mapped.id === item.id) ?? null;
      }
    } catch (error) {
      console.warn("Failed to load promotion by id.", error);
    }

    return null;
  };

  const ensurePromotionForSlug = async (slug) => {
    const existing = findPromotionBySlug(slug);
    if (existing) {
      return existing;
    }

    const promotionId = resolvePromotionIdFromSlug(slug);
    if (promotionId == null) {
      return null;
    }

    const fromList = promotions.value.find((item) => item.id === promotionId);
    if (fromList) {
      return fromList;
    }

    return loadPromotionById(promotionId);
  };

  const findRawPromotionBySlug = (slug, locale) => {
    const index = rawPromotions.value.findIndex((item, itemIndex) => {
      const itemSlug = toPortalPromotionSlug(item, locale, itemIndex);
      return itemSlug === slug;
    });

    if (index === -1) {
      return null;
    }

    return {
      raw: rawPromotions.value[index],
      mapped: promotions.value[index] ?? null,
      index,
    };
  };

  const activePromotionsCount = computed(
    () => promotions.value.filter((item) => item.isActive !== false).length,
  );

  const signInRequiredCount = computed(
    () =>
      promotions.value.filter(
        (item) => item.requiresSignIn && !item.signedIn,
      ).length,
  );

  const filteredPromotions = computed(() =>
    filterPortalPromotions(promotions.value, activeFilter.value),
  );

  watch(currentLocale, () => {
    if (rawPromotions.value.length > 0) {
      applyPromotionLocale();
    }
  });

  if (loadOnMount) {
    onMounted(() => {
      loadPromotions();
    });
  }

  return {
    promotions,
    filteredPromotions,
    activeFilter,
    rawPromotions,
    promotionsUseMock,
    promotionsLoading,
    signingInPromotionId,
    activePromotionsCount,
    signInRequiredCount,
    loadPromotions,
    handlePromotionSignIn,
    findPromotionBySlug,
    findRawPromotionBySlug,
    loadPromotionById,
    ensurePromotionForSlug,
    applyPromotionLocale,
  };
}
